import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';

export default function ClientFormsPage() {
  const { memberships } = useOrg();
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);

  // Aggregate across every practice the patient belongs to
  const fetchForms = useCallback(async () => {
    if (!memberships?.length) { setForms([]); setQuestionnaires([]); setLoading(false); return; }
    try {
      // Practice-built questionnaires (custom forms)
      Promise.all(
        memberships.map(m =>
          api.getMyCustomForms(m.org_id)
            .then(rows => (rows || []).map(r => ({ ...r, _orgId: m.org_id, _orgName: m.organization?.name })))
            .catch(() => [])
        )
      ).then(results => setQuestionnaires(results.flat()));

      const results = await Promise.all(
        memberships.map(m =>
          api.getMyForms(m.org_id)
            .then(rows => (rows || []).map(r => ({ ...r, _orgName: m.organization?.name })))
            .catch(() => [])
        )
      );
      const merged = results.flat().sort((a, b) => {
        // pending first, then by appointment date asc, signed by signed_at desc
        if (a.status !== b.status) return a.status === 'pending' ? -1 : 1;
        const aKey = a.appointment_date || a.signed_at || a.created_at;
        const bKey = b.appointment_date || b.signed_at || b.created_at;
        return new Date(bKey) - new Date(aKey);
      });
      setForms(merged);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [memberships]);

  useEffect(() => { fetchForms(); }, [fetchForms]);

  const pending = forms.filter(f => f.status === 'pending');
  const signed = forms.filter(f => f.status === 'signed');
  const openQuestionnaires = questionnaires.filter(q => !q.submitted_at);
  const doneQuestionnaires = questionnaires.filter(q => q.submitted_at);

  return (
    <div className="flex-1 bg-primary">
      <div className="max-w-[760px] mx-auto px-4 md:px-7 py-6">
        <h1 className="text-[22px] font-bold text-primary-c mb-1">My Forms</h1>
        <p className="text-[12px] text-muted-c mb-6">
          Complete these documents before your appointment. Once signed, they're saved to your account.
        </p>

        {loading ? (
          <div className="space-y-2">{[1, 2].map(i => <div key={i} className="h-20 bg-tertiary rounded-xl animate-pulse" />)}</div>
        ) : (
          <>
            {(openQuestionnaires.length > 0 || doneQuestionnaires.length > 0) && (
              <Section
                title="Questionnaires"
                count={openQuestionnaires.length}
                emptyLabel="All questionnaires completed — thank you!"
                accent
              >
                {openQuestionnaires.map(q => (
                  <div key={q.id} className="bg-elevated rounded-xl border border-theme p-4 flex items-center gap-3.5">
                    <div className="w-11 h-11 rounded-[12px] bg-accent-glow flex items-center justify-center flex-shrink-0">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0DA7CA" strokeWidth="2">
                        <rect x="4" y="3" width="16" height="18" rx="2" />
                        <path d="M9 8h6M9 12h6M9 16h3" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-primary-c truncate">{q.title}</div>
                      <div className="text-[11px] text-muted-c mt-0.5 truncate">
                        {q._orgName}<span className="opacity-40 mx-1">&middot;</span>{(q.fields || []).length} questions
                      </div>
                    </div>
                    <button
                      onClick={() => navigate(`/client/questionnaires/${q.id}`, { state: { orgId: q._orgId, orgName: q._orgName } })}
                      className="px-3.5 py-2 rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all"
                    >
                      Fill out
                    </button>
                  </div>
                ))}
              </Section>
            )}

            <Section
              title="Pending"
              count={pending.length}
              emptyLabel="No forms awaiting your signature"
              accent
            >
              {pending.map(f => (
                <FormRow
                  key={f.id}
                  form={f}
                  actionLabel="Fill & Sign"
                  onAction={() => navigate(`/client/forms/${f.id}`, { state: { orgId: f.org_id || f._orgId } })}
                />
              ))}
            </Section>

            <Section
              title="Completed"
              count={signed.length}
              emptyLabel="No completed forms yet"
            >
              {signed.map(f => (
                <FormRow
                  key={f.id}
                  form={f}
                  actionLabel="View"
                  subtle
                  onAction={() => navigate(`/client/forms/${f.id}`, { state: { orgId: f.org_id || f._orgId } })}
                />
              ))}
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, count, emptyLabel, accent, children }) {
  const hasChildren = React.Children.count(children) > 0;
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-[14px] font-semibold text-primary-c">{title}</h2>
        {count > 0 && (
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
            accent ? 'bg-accent-glow text-accent-light' : 'bg-tertiary text-muted-c'
          }`}>
            {count}
          </span>
        )}
      </div>
      {hasChildren ? (
        <div className="space-y-2">{children}</div>
      ) : (
        <div className="bg-tertiary border border-theme rounded-xl p-5 text-center">
          <p className="text-[12px] text-muted-c">{emptyLabel}</p>
        </div>
      )}
    </div>
  );
}

function FormRow({ form, actionLabel, subtle, onAction }) {
  const name = form.template?.name || 'Untitled form';
  const when = form.status === 'signed' && form.signed_at
    ? `Signed ${format(new Date(form.signed_at), 'MMM d, yyyy')}`
    : form.appointment_date
      ? `For appointment on ${format(new Date(form.appointment_date), 'MMM d, yyyy')}`
      : `Created ${format(new Date(form.created_at), 'MMM d, yyyy')}`;

  return (
    <div className="bg-elevated rounded-xl border border-theme p-4 flex items-center gap-3.5">
      <div className={`w-11 h-11 rounded-[12px] flex items-center justify-center flex-shrink-0 ${
        form.status === 'signed' ? 'bg-success-soft' : 'bg-accent-glow'
      }`}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={form.status === 'signed' ? '#10B981' : '#0DA7CA'} strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-primary-c truncate">{name}</div>
        <div className="text-[11px] text-muted-c mt-0.5 truncate">
          {form._orgName && <span>{form._orgName}<span className="opacity-40 mx-1">&middot;</span></span>}
          {when}
        </div>
      </div>
      <button
        onClick={onAction}
        className={`px-3.5 py-2 rounded-[10px] text-[12px] font-semibold transition-all ${
          subtle
            ? 'text-primary-c bg-tertiary border border-theme hover:bg-hover-surface'
            : 'text-white gradient-accent shadow-glow hover:-translate-y-0.5'
        }`}
      >
        {actionLabel}
      </button>
    </div>
  );
}
